import { useMutation } from '@apollo/client';
import canCreateApplication, {
  ApplicationWithCase,
  GENERIC_REAPPLICATION_ERROR,
} from '@bybeam/platform-lib/application/reapplication';
import { Application, CreateApplicationInput, MutationResponse } from '@bybeam/platform-types';
import useProgram from '@platform-ui-common/hooks/contexts/useProgram.js';
import useErrorIndicator from '@platform-ui-common/hooks/useErrorIndicator.js';
import MutationHookResponse from '@platform-ui/@types/mutation.js';
import CreateApplicationMutation from '@platform-ui/application/graphql/CreateApplication.graphql';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { getApplicantSections } from '@platform-ui/utilities/application/config.js';
import { getApplicationConfiguration } from '@platform-ui/utilities/application/configs/index.js';
import { getSectionRoute } from '@platform-ui/utilities/application/section.js';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import ApplicantRoutes from '../utils/ApplicantRoutes.js';
import useUserApplications from './useUserApplications.js';

export default function useCreateApplication(): MutationHookResponse<void> {
  const [createApplication, { data, loading, error: transportError }] = useMutation<
    { application: { create: MutationResponse<Application> } },
    { input: Pick<CreateApplicationInput, 'programId'> }
  >(CreateApplicationMutation);
  const history = useHistory();
  const program = useProgram();
  const { user } = useAuth();
  const config = getApplicationConfiguration(program, user.core);
  const applications = useUserApplications();
  const showError = useErrorIndicator();

  // biome-ignore lint/correctness/useExhaustiveDependencies: wants doMutation itself as a dep
  const doMutation = useCallback(async () => {
    if (!canCreateApplication(program, applications as ApplicationWithCase[], user.core)) {
      history.push(ApplicantRoutes.APPLICATION_PORTAL);
      return true;
    }

    const {
      data: {
        application: {
          create: { metadata, record },
        },
      },
    } = await createApplication({ variables: { input: { programId: program.id } } });
    if (metadata.status === 201) {
      history.push(
        makeRoute(getSectionRoute(getApplicantSections(config)[0], record.answers), {
          applicationId: metadata.id,
        }),
      );
      return true;
    }

    const error = metadata.errors?.[0] ?? '';
    if (error.includes(GENERIC_REAPPLICATION_ERROR)) {
      history.push(ApplicantRoutes.APPLICATION_PORTAL);
      showError('You are not eligible to start a new application.');
    } else showError('There was an error starting your application.', doMutation);
    return false;
  }, [applications, config, createApplication, history, program, showError]);

  const error = transportError
    ? 'Something went wrong!'
    : data?.application.create.metadata.errors?.[0];

  return useMemo(() => ({ doMutation, loading, error }), [doMutation, loading, error]);
}
